import React, { useState, useEffect } from 'react';
import {
    Container,
    Typography,
    Paper,
    Box,
    Button,
    Alert,
    Breadcrumbs,
    CircularProgress,
    Chip,
    InputAdornment,
    TextField,
    Stack,
    Tooltip,
    Card,
    CardContent,
    Grid,
    Select,
    MenuItem,
    FormControl,
    InputLabel
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { DataGrid, GridColDef, GridRenderCellParams, GridSortModel, GridPaginationModel } from '@mui/x-data-grid';
import { styled } from '@mui/material/styles';
import HomeIcon from '@mui/icons-material/Home';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import DownloadIcon from '@mui/icons-material/Download';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import PeopleIcon from '@mui/icons-material/People';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import ReceiptIcon from '@mui/icons-material/Receipt';
import { transactionAPI, memberProfileAPI } from '../../services/api';
import { Transaction } from '../../types';
import { reportAPI } from '../../services/reportAPI';

const StyledGridOverlay = styled('div')(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    '& .no-rows-primary': {
        fill: '#3D4751',
        ...theme.applyStyles('light', {
            fill: '#AEB8C2',
        }),
    },
    '& .no-rows-secondary': {
        fill: '#1D2126',
        ...theme.applyStyles('light', {
            fill: '#E8EAED',
        }),
    },
}));

interface MemberInArrears {
    memberId: string;
    memberName: string;
    memberEmail: string;
    membershipTier: string;
    totalFailedAmount: number;
    failureCount: number;
    earliestTransactionDate: string;
    lastFailureDate: string;
    lastFailureReason: string;
    transactions: Transaction[];
}

const ArrearsReport: React.FC = () => {
    const [membersInArrears, setMembersInArrears] = useState<MemberInArrears[]>([]);
    const [filteredMembers, setFilteredMembers] = useState<MemberInArrears[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [searchText, setSearchText] = useState<string>('');
    const [ageFilter, setAgeFilter] = useState<string>('all');

    // Sorting state
    const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'lastFailureDate', sort: 'desc' }]);

    // Pagination
    const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
        page: 0,
        pageSize: 100,
    });

    // Fetch failed transactions and aggregate by member
    const fetchMembersInArrears = async () => {
        setLoading(true);
        try {
            const response = await transactionAPI.getAll({
                status: 'failed',
                limit: 10000 // Get all failed transactions
            });

            const failedTxns = Array.isArray(response.data.transactions)
                ? response.data.transactions
                : Array.isArray(response.data)
                    ? response.data
                    : [];

            // Get unique member IDs
            const uniqueMemberIds: string[] = [...new Set<string>(failedTxns.map((txn: Transaction) => txn.memberId as string))];

            // Fetch member profiles to get membership tier information
            const memberProfilesMap = new Map<string, any>();

            // Fetch all member profiles in parallel
            try {
                const memberProfilePromises = uniqueMemberIds.map(async (memberId: string) => {
                    try {
                        const memberResponse = await memberProfileAPI.getMemberProfileById(memberId);
                        return memberResponse.data;
                    } catch (err) {
                        console.error(`Error fetching member profile ${memberId}:`, err);
                        return null;
                    }
                });

                const memberProfiles = await Promise.all(memberProfilePromises);
                memberProfiles.forEach(profile => {
                    if (profile) {
                        memberProfilesMap.set(profile.id, profile);
                    }
                });
            } catch (err) {
                console.error('Error fetching member profiles:', err);
            }

            // Group by member and calculate totals
            const memberMap = new Map<string, MemberInArrears>();

            failedTxns.forEach((txn: Transaction) => {
                const txnMemberId = txn.memberId as string;

                if (!memberMap.has(txnMemberId)) {
                    // Get membership tier from fetched member profile
                    const memberProfile = memberProfilesMap.get(txnMemberId);
                    const membershipTierName = memberProfile?.membershipTierName ||
                        memberProfile?.membershipTier?.name ||
                        'N/A';

                    memberMap.set(txnMemberId, {
                        memberId: txnMemberId,
                        memberName: txn.memberProfile
                            ? `${txn.memberProfile.firstName} ${txn.memberProfile.lastName}`
                            : memberProfile
                                ? `${memberProfile.firstName} ${memberProfile.lastName}`
                                : 'Unknown Member',
                        memberEmail: txn.memberProfile?.email || memberProfile?.email || 'N/A',
                        membershipTier: membershipTierName,
                        totalFailedAmount: 0,
                        failureCount: 0,
                        earliestTransactionDate: txn.createdAt || '',
                        lastFailureDate: txn.metadata?.lastErrorAt || txn.updatedAt || '',
                        lastFailureReason: txn.metadata?.lastError || 'Payment failed',
                        transactions: []
                    });
                }

                const memberData = memberMap.get(txnMemberId)!;
                memberData.totalFailedAmount += Number(txn.totalAmount) || 0;
                memberData.failureCount += 1;
                memberData.transactions.push(txn);

                // Update to earliest transaction date
                const txnCreatedDate = txn.createdAt || '';
                if (txnCreatedDate && (!memberData.earliestTransactionDate || txnCreatedDate < memberData.earliestTransactionDate)) {
                    memberData.earliestTransactionDate = txnCreatedDate;
                }

                // Update to most recent failure
                const currentFailureDate = txn.metadata?.lastErrorAt || txn.updatedAt || '';
                if (currentFailureDate > memberData.lastFailureDate) {
                    memberData.lastFailureDate = currentFailureDate;
                    memberData.lastFailureReason = txn.metadata?.lastError || 'Payment failed';
                }
            });

            // Convert to array
            const aggregatedMembers = Array.from(memberMap.values());
            setMembersInArrears(aggregatedMembers);
            setFilteredMembers(aggregatedMembers);
            setError(null);
        } catch (err) {
            console.error('Error fetching members in arrears:', err);
            setError('Failed to load members in arrears. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchMembersInArrears();
        // Log report access
        reportAPI.logReportAccess('arrears-report');
    }, []);

    // Handle search and age filtering
    useEffect(() => {
        let filtered = [...membersInArrears];

        // Apply search filter
        if (searchText.trim()) {
            const searchLower = searchText.toLowerCase();
            filtered = filtered.filter(member =>
                member.memberName.toLowerCase().includes(searchLower) ||
                member.memberEmail.toLowerCase().includes(searchLower) ||
                member.lastFailureReason.toLowerCase().includes(searchLower)
            );
        }

        // Apply age filter based on transaction date
        if (ageFilter !== 'all') {
            const now = new Date();
            now.setHours(0, 0, 0, 0); // Reset to start of day for accurate comparison
            
            filtered = filtered.filter(member => {
                if (!member.earliestTransactionDate) return false;
                
                // Parse the date and reset to start of day to avoid timezone issues
                const transactionDate = new Date(member.earliestTransactionDate);
                transactionDate.setHours(0, 0, 0, 0);
                
                const daysDiff = Math.floor((now.getTime() - transactionDate.getTime()) / (1000 * 60 * 60 * 24));

                if (ageFilter === '30') {
                    return daysDiff > 30 && daysDiff <= 60;
                } else if (ageFilter === '60') {
                    return daysDiff > 60 && daysDiff <= 90;
                } else if (ageFilter === '90') {
                    return daysDiff > 90;
                }
                return true;
            });
        }

        setFilteredMembers(filtered);
        setPaginationModel({ ...paginationModel, page: 0 });
    }, [searchText, ageFilter, membersInArrears]);

    // Clear filters
    const clearFilters = () => {
        setSearchText('');
        setAgeFilter('all');
        setSortModel([{ field: 'lastFailureDate', sort: 'desc' }]);
        setPaginationModel({ ...paginationModel, page: 0 });
    };

    // Format currency
    const formatCurrency = (amount: number | string | undefined) => {
        if (amount === undefined || amount === null) return '$0.00';
        const numericAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(numericAmount);
    };

    // Format date
    const formatDate = (dateString: string) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    };

    // Export to CSV
    const exportToCSV = async () => {
        if (filteredMembers.length === 0) {
            setError('No members to export');
            return;
        }

        try {
            // Helper function to escape and quote CSV fields
            const escapeCSVField = (field: any): string => {
                if (field === null || field === undefined) return '""';
                const stringField = String(field);
                const escapedField = stringField.replace(/"/g, '""');
                return `"${escapedField}"`;
            };

            // Define CSV headers
            const headers = [
                'Member Name',
                'Email',
                'Membership Tier',
                'Total Failed Amount',
                'Number of Failed Transactions',
                'Transaction Date',
                'Last Failure',
                'Failure Reason'
            ];

            // Create CSV rows
            const rows = filteredMembers.map((member) => {
                return [
                    escapeCSVField(member.memberName),
                    escapeCSVField(member.memberEmail),
                    escapeCSVField(member.membershipTier),
                    escapeCSVField(formatCurrency(member.totalFailedAmount)),
                    escapeCSVField(member.failureCount),
                    escapeCSVField(formatDate(member.earliestTransactionDate)),
                    escapeCSVField(formatDate(member.lastFailureDate)),
                    escapeCSVField(member.lastFailureReason)
                ].join(',');
            });

            // Combine headers and rows
            const csvContent = [headers.map(h => escapeCSVField(h)).join(','), ...rows].join('\n');

            // Create blob and download
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);

            const dateStr = new Date().toISOString().split('T')[0];
            const filename = `arrears_report_${dateStr}.csv`;

            link.setAttribute('href', url);
            link.setAttribute('download', filename);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            setSuccess(`Exported ${filteredMembers.length} members to CSV`);
            setTimeout(() => setSuccess(null), 3000);
        } catch (err) {
            console.error('Error exporting to CSV:', err);
            setError('Failed to export to CSV');
        }
    };

    function CustomNoRowsOverlay() {
        return (
            <StyledGridOverlay>
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    width={96}
                    viewBox="0 0 452 257"
                    aria-hidden
                    focusable="false"
                >
                    <path
                        className="no-rows-primary"
                        d="M348 69c-46.392 0-84 37.608-84 84s37.608 84 84 84 84-37.608 84-84-37.608-84-84-84Zm-104 84c0-57.438 46.562-104 104-104s104 46.562 104 104-46.562 104-104 104-104-46.562-104-104Z"
                    />
                    <path
                        className="no-rows-primary"
                        d="M308.929 113.929c3.905-3.905 10.237-3.905 14.142 0l63.64 63.64c3.905 3.905 3.905 10.236 0 14.142-3.906 3.905-10.237 3.905-14.142 0l-63.64-63.64c-3.905-3.905-3.905-10.237 0-14.142Z"
                    />
                    <path
                        className="no-rows-primary"
                        d="M308.929 191.711c-3.905-3.906-3.905-10.237 0-14.142l63.64-63.64c3.905-3.905 10.236-3.905 14.142 0 3.905 3.905 3.905 10.237 0 14.142l-63.64 63.64c-3.905 3.905-10.237 3.905-14.142 0Z"
                    />
                    <path
                        className="no-rows-secondary"
                        d="M0 10C0 4.477 4.477 0 10 0h380c5.523 0 10 4.477 10 10s-4.477 10-10 10H10C4.477 20 0 15.523 0 10ZM0 59c0-5.523 4.477-10 10-10h231c5.523 0 10 4.477 10 10s-4.477 10-10 10H10C4.477 69 0 64.523 0 59ZM0 106c0-5.523 4.477-10 10-10h203c5.523 0 10 4.477 10 10s-4.477 10-10 10H10c-5.523 0-10-4.477-10-10ZM0 153c0-5.523 4.477-10 10-10h195.5c5.523 0 10 4.477 10 10s-4.477 10-10 10H10c-5.523 0-10-4.477-10-10ZM0 200c0-5.523 4.477-10 10-10h203c5.523 0 10 4.477 10 10s-4.477 10-10 10H10c-5.523 0-10-4.477-10-10ZM0 247c0-5.523 4.477-10 10-10h231c5.523 0 10 4.477 10 10s-4.477 10-10 10H10c-5.523 0-10-4.477-10-10Z"
                    />
                </svg>
                <Box sx={{ mt: 2 }}>No members in arrears</Box>
            </StyledGridOverlay>
        );
    }

    // Define columns for DataGrid
    const columns: GridColDef[] = [
        {
            field: 'earliestTransactionDate',
            headerName: 'Transaction Date',
            width: 140,
            renderCell: (params: GridRenderCellParams) => formatDate(params.value),
        },
        {
            field: 'memberName',
            headerName: 'Member Name',
            width: 180,
            renderCell: (params: GridRenderCellParams) => {
                return (
                    <RouterLink
                        to={`/member-profiles/${params.row.memberId}`}
                        style={{
                            textDecoration: 'none',
                            color: 'inherit',
                        }}
                    >
                        {params.value}
                    </RouterLink>
                );
            },
        },
        {
            field: 'memberEmail',
            headerName: 'Email',
            width: 220,
        },
        {
            field: 'membershipTier',
            headerName: 'Membership Tier',
            width: 160,
        },
        {
            field: 'totalFailedAmount',
            headerName: 'Total Amount',
            width: 130,
            renderCell: (params: GridRenderCellParams) => (
                <Typography variant="body2" fontWeight="bold">
                    {formatCurrency(params.value)}
                </Typography>
            ),
        },
        {
            field: 'failureCount',
            headerName: 'Transactions',
            width: 110,
            renderCell: (params: GridRenderCellParams) => (
                <Typography variant="body2">
                    {params.value}
                </Typography>
            ),
        },
        {
            field: 'lastFailureDate',
            headerName: 'Last Failure',
            width: 140,
            renderCell: (params: GridRenderCellParams) => formatDate(params.value),
        },
        {
            field: 'lastFailureReason',
            headerName: 'Failure Reason',
            flex: 1,
            minWidth: 200,
            renderCell: (params: GridRenderCellParams) => {
                const reason = params.value || 'Payment failed';
                return (
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                            whiteSpace: 'normal',
                            wordWrap: 'break-word',
                            lineHeight: 1.5,
                            py: 1
                        }}
                    >
                        {reason}
                    </Typography>
                );
            },
        },
    ];

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            {/* Breadcrumbs */}
            <Breadcrumbs
                separator={<NavigateNextIcon fontSize="small" />}
                aria-label="breadcrumb"
                sx={{ mb: 3 }}
            >
                <RouterLink
                    style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', color: 'inherit' }}
                    to="/dashboard"
                >
                    <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                    Dashboard
                </RouterLink>
                <RouterLink
                    style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', color: 'inherit' }}
                    to="/reports"
                >
                    Reports
                </RouterLink>
                <Typography color="text.primary">Arrears Report</Typography>
            </Breadcrumbs>

            <Box sx={{ mb: 3 }}>
                <Typography variant="h4" component="h1" gutterBottom>
                    Arrears Report
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Members with failed payment transactions
                </Typography>
            </Box>

            {/* Success message */}
            {success && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
                    {success}
                </Alert>
            )}

            {/* Error message */}
            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {/* Summary Cards */}
            {!loading && (
                <Grid container spacing={3} sx={{ mb: 3 }}>
                    <Grid size={{ xs: 12, sm: 4 }}>
                        <Card elevation={2}>
                            <CardContent>
                                <Box display="flex" alignItems="center" mb={1}>
                                    <PeopleIcon sx={{ mr: 1, color: 'primary.main' }} />
                                    <Typography color="textSecondary" variant="body2">
                                        Members in Arrears
                                    </Typography>
                                </Box>
                                <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                                    {filteredMembers.length}
                                </Typography>
                                <Typography color="textSecondary" variant="body2" sx={{ mt: 1 }}>
                                    With failed payments
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                        <Card elevation={2}>
                            <CardContent>
                                <Box display="flex" alignItems="center" mb={1}>
                                    <AttachMoneyIcon sx={{ mr: 1, color: 'error.main' }} />
                                    <Typography color="textSecondary" variant="body2">
                                        Total Outstanding
                                    </Typography>
                                </Box>
                                <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                                    {formatCurrency(filteredMembers.reduce((sum, m) => sum + m.totalFailedAmount, 0))}
                                </Typography>
                                <Typography color="textSecondary" variant="body2" sx={{ mt: 1 }}>
                                    Across all members
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                        <Card elevation={2}>
                            <CardContent>
                                <Box display="flex" alignItems="center" mb={1}>
                                    <ReceiptIcon sx={{ mr: 1, color: 'warning.main' }} />
                                    <Typography color="textSecondary" variant="body2">
                                        Total Transactions
                                    </Typography>
                                </Box>
                                <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                                    {filteredMembers.reduce((sum, m) => sum + m.failureCount, 0)}
                                </Typography>
                                <Typography color="textSecondary" variant="body2" sx={{ mt: 1 }}>
                                    Failed payment attempts
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            )}

            {/* Main content */}
            <Paper sx={{ p: 3 }}>
                {/* Filters */}
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        <TextField
                            placeholder="Search by member name, email, or reason..."
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            size="small"
                            sx={{ minWidth: 400 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                            }}
                        />

                        <FormControl size="small" sx={{ minWidth: 150 }}>
                            <InputLabel id="age-filter-label">Age</InputLabel>
                            <Select
                                labelId="age-filter-label"
                                value={ageFilter}
                                label="Age"
                                onChange={(e) => setAgeFilter(e.target.value)}
                            >
                                <MenuItem value="all">All</MenuItem>
                                <MenuItem value="30">&gt; 30 Days</MenuItem>
                                <MenuItem value="60">&gt; 60 Days</MenuItem>
                                <MenuItem value="90">&gt; 90 Days</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>

                    <Button
                        variant="outlined"
                        startIcon={<DownloadIcon />}
                        onClick={exportToCSV}
                        disabled={filteredMembers.length === 0}
                        size="small"
                    >
                        Export to CSV
                    </Button>
                </Box>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Box sx={{ height: 600, width: '100%' }}>
                        <DataGrid
                            rows={filteredMembers}
                            columns={columns}
                            getRowId={(row) => row.memberId}
                            paginationModel={paginationModel}
                            onPaginationModelChange={setPaginationModel}
                            pageSizeOptions={[25, 50, 100]}
                            sortingMode="client"
                            sortModel={sortModel}
                            onSortModelChange={setSortModel}
                            loading={loading}
                            checkboxSelection={false}
                            disableRowSelectionOnClick
                            slots={{
                                noRowsOverlay: CustomNoRowsOverlay,
                                noResultsOverlay: CustomNoRowsOverlay,
                            }}
                            slotProps={{
                                loadingOverlay: {
                                    variant: 'linear-progress',
                                    noRowsVariant: 'skeleton',
                                },
                            }}
                            sx={{
                                '& .MuiDataGrid-row': {
                                    cursor: 'pointer',
                                },
                                '& .MuiDataGrid-cell': {
                                    display: 'flex',
                                    alignItems: 'center',
                                },
                            }}
                            getRowHeight={() => 'auto'}
                        />
                    </Box>
                )}
            </Paper>
        </Container>
    );
};

export default ArrearsReport;
